import { useLayoutEffect, type RefObject } from 'react'

/**
 * #294 redesign — publish an element's height as a CSS variable on <html> (e.g. `--crit-bar-h`), so fixed
 * columns elsewhere (the critical popups, the normal toasts) can start below it. Follows the element's
 * size (ResizeObserver: a bar that wraps on a phone, a popup added or closed). `extra` is added when the
 * height is above 0 (the gap below the element). Removed when `active` is false and on unmount.
 */
export function useElementHeightVar(ref: RefObject<HTMLElement>, name: string, active: boolean, extra = 0): void {
  useLayoutEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null
    if (!root) return
    const el = ref.current
    if (!active || !el) {
      root.style.removeProperty(name)
      return
    }
    const update = () => {
      const h = Math.round(el.getBoundingClientRect().height)
      root.style.setProperty(name, `${h > 0 ? h + extra : 0}px`)
    }
    update()
    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update)
      observer.observe(el)
    }
    return () => {
      observer?.disconnect()
      root.style.removeProperty(name)
    }
  }, [ref, name, active, extra])
}

/**
 * #294 redesign (review 2026-10-12) — publish where an element's BOTTOM EDGE is (px from the top of the
 * window) as a CSS variable on <html> (the alert bar: `--crit-bar-bottom`), so the fixed columns (the critical
 * popups, the normal toasts) start below it whatever sits above it: the superuser's environment banner, a
 * header that changes height. Follows the element's size and the size of everything above it in its parent
 * (ResizeObserver), an element added or removed above it (MutationObserver), and the window's resize and
 * scroll. Removed when `active` is false and on unmount (the CSS then falls back to below the header).
 */
export function useElementBottomVar(ref: RefObject<HTMLElement>, name: string, active: boolean): void {
  useLayoutEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null
    if (!root) return
    const el = ref.current
    if (!active || !el) {
      root.style.removeProperty(name)
      return
    }
    const update = () => {
      root.style.setProperty(name, `${Math.max(0, Math.round(el.getBoundingClientRect().bottom))}px`)
    }
    update()
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    const observeAbove = () => {
      if (!resize) return
      resize.disconnect()
      resize.observe(el)
      for (let above = el.previousElementSibling; above; above = above.previousElementSibling) resize.observe(above)
    }
    observeAbove()
    const parent = el.parentElement
    const added = parent && typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => { observeAbove(); update() })
      : null
    if (parent) added?.observe(parent, { childList: true })
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, { passive: true })
    return () => {
      resize?.disconnect()
      added?.disconnect()
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update)
      root.style.removeProperty(name)
    }
  }, [ref, name, active])
}
